import React, { useRef, useEffect } from 'react';
import BlogRoll, { IBlogPost } from '../BlogRoll';
import Hero from 'tbk-components/src/components/BlogRoll/BlogRollHero';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import cn from 'classnames';

/**
 * @usePostData
 * @post_type post
 */
export interface IBlogRollFeaturedPost extends IBlogPost {}

/**
 * @useTermData
 * @taxonomy post_tag category
 */
export interface IBlogRollFilterTerms {
	term_id: number;
	name: string;
	taxonomy: string;
}

export interface IBlogRollWithFeaturedPostProps extends IBaseElementProps {
	hero?: IBlogRollFeaturedPost;
	showHeading?: boolean;
	/**
	 * The heading appears at the top of the widget
	 */
	heading?: string;
	/**
	 * Limit the posts to only those in the selected terms.
	 */
	terms?: IBlogRollFilterTerms[];
	/**
	 * A button to view other posts
	 */
	showSeeMore?: boolean;
	seeMoreLabel?: string;
	/**
	 * The URL the "See More" button will go to
	 */
	archivePage?: string;
	showPagination?: boolean;
	showExcerpt?: boolean;
	/**
	 * Show the "Read on" button on the tile
	 */
	showButton?: boolean;
	/**
	 * Show the author byline on the tile
	 */
	showByline?: boolean;
	/**
	 * True to show only the filter bar in this widget
	 */
	filterBarOnly?: boolean;
	/**
	 * The URL of the page the filter bar directs to
	 */
	filterBarTargetUrl?: string;
	/**
	 * The message to show if no results found
	 */
	noResultsMessage?: string;
}

/**
 * Show the BlogRoll widget, with ability to pin a featured post
 *
 * @block
 * @requiresHooks
 * @icon id-alt
 */
const BlogRollWithFeaturedPost: React.FC<IBlogRollWithFeaturedPostProps> = ({
	className,
	classNames = [],
	hero,
	heading,
	archivePage,
	terms,
	showHeading = false,
	showSeeMore = false,
	seeMoreLabel = 'Read on',
	showPagination = true,
	showExcerpt = true,
	showButton = true,
	showByline = false,
	filterBarOnly = false,
	filterBarTargetUrl = '',
	noResultsMessage = 'No results found.  Please adjust your filters',
}) => {
	const ref = useRef<HTMLDivElement>(null);

	const fetchArgs: any = {
		post_type: ['post'],
	};
	if (hero) {
		fetchArgs.post__not_in = [hero['ID']]; // written like this gets around IBlogRollFeaturedPost not having an ID
	}
	if (terms) {
		terms.forEach((term) => {
			if (!fetchArgs[term.taxonomy]) {
				fetchArgs[term.taxonomy] = [];
			}
			fetchArgs[term.taxonomy].push(term.term_id);
		});
	}

	return (
		<div
			className={
				className ||
				cn('blogroll-page blogroll-withfeaturedpost', ...classNames)
			}
			ref={ref}
		>
			{heading && <h1>{heading}</h1>}

			{hero && <Hero post={hero} readMoreLabel={seeMoreLabel} />}

			<div className="blogroll__inner">
				<BlogRoll
					heading={heading}
					archivePage={archivePage}
					showHeading={showHeading}
					showSeeMore={showSeeMore}
					seeMoreLabel={seeMoreLabel}
					showPagination={showPagination}
					showExcerpt={showExcerpt}
					showButton={showButton}
					showByline={showByline}
					filterBarOnly={filterBarOnly}
					filterBarTargetUrl={filterBarTargetUrl}
					fetchArgs={fetchArgs}
					noResultsMessage={noResultsMessage}
				/>
			</div>
		</div>
	);
};

export default BlogRollWithFeaturedPost;
